/**
 * 往期列表顶上那一行颜色说明。
 *
 * <p>Draws History 和 Pick 4 / Pick 4 Hourly 的 History 页签共用这一个组件 ——
 * 两边的竖条是同一套语义，说明也必须逐字相同。什么时候显示哪几项由
 * `shared/game/historyLegend.ts` 的 `historyLegendView` 决定（纯函数，有单测），
 * 这里只管画。
 *
 * <p><b>色块从 CSS 变量取，不写死颜色。</b> 图例和行内竖条必须是同一个色值 ——
 * 抄一遍十六进制，主题一改就会变成「图例说金色，行里是另一种金色」。
 *
 * <p>色块 `aria-hidden`，文字本身就是说明；行内竖条的读屏器文案在各自行的
 * `aria-label` 里，两条通道各自完整，不互相依赖。
 */

import { useI18nStore } from '../i18n/i18nStore';
import type { MessageKey } from '../i18n/messages';
import type { LegendMark } from '../game/historyLegend';

const LABEL_KEY: Record<LegendMark, MessageKey> = {
  won: 'histLegendWon',
  played: 'histLegendPlayed',
  none: 'histLegendNone',
  jackpot: 'histLegendJackpot',
};

export function HistoryLegend({ items, noRecent, noRecentKey }: {
  items: LegendMark[];
  /** 非 null 时显示「最近 %n% 期你没有参与」 */
  noRecent: number | null;
  /** 那句话的文案键 —— 抽奖说「期」、Pick 4 说「轮」，两边措辞不同 */
  noRecentKey: MessageKey;
}) {
  const t = useI18nStore((state) => state.t);
  if (items.length === 0 && noRecent === null) return null;
  return (
    <div className="hist-legend">
      {items.length ? (
        <ul className="hist-legend-items" aria-label={t('histLegendAria')}>
          {items.map((mark) => (
            <li key={mark}>
              <i className={`hist-legend-mark is-${mark}`} aria-hidden="true" />
              {t(LABEL_KEY[mark])}
            </li>
          ))}
        </ul>
      ) : null}
      {noRecent !== null ? (
        <p className="hist-legend-note">
          {t(noRecentKey).replace('%n%', String(noRecent))}
        </p>
      ) : null}
    </div>
  );
}
